import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import OriginTransactionsModal from "@/components/OriginTransactionsModal";
import { Plus, X, Trash2, FileText, Upload } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const fmt = (n, d = 2) => (n ?? 0).toLocaleString("en-ZA", { minimumFractionDigits: d, maximumFractionDigits: d });

export default function GreenInventory() {
  const { user } = useAuth();
  const canWrite = ["Owner", "ProductionManager", "Bookkeeper"].includes(user?.role);
  const isOwner = user?.role === "Owner";
  const fileRef = useRef(null);

  const [tab, setTab] = useState("on-hand");
  const [onHand, setOnHand] = useState([]);
  const [lots, setLots] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [origins, setOrigins] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [openOriginId, setOpenOriginId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [asOf, setAsOf] = useState("");          // "" = live (today)
  const [asOfBusy, setAsOfBusy] = useState(false);
  const [form, setForm] = useState({
    origin_id: "", supplier_id: "", lot_ref: "",
    purchase_date: new Date().toISOString().slice(0, 10),
    weight_kg: "", unit_cost_per_kg: "", notes: "",
  });

  const load = async () => {
    setLoading(true);
    const ohUrl = asOf ? `/green-lots/on-hand?as_of=${asOf}` : "/green-lots/on-hand";
    const [oh, ls, adj, os, sp] = await Promise.all([
      api.get(ohUrl),
      api.get("/green-lots"),
      api.get("/inventory-adjustments"),
      api.get("/origins"),
      api.get("/suppliers"),
    ]);
    setOnHand(oh.data);
    setLots(ls.data);
    setAdjustments(adj.data);
    setOrigins(os.data);
    setSuppliers(sp.data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Re-fetch only the on-hand valuation when the as-of date changes
  // (the other collections are global so they don't need re-fetching).
  const reloadAsOf = async (newDate) => {
    setAsOf(newDate);
    setAsOfBusy(true);
    try {
      const url = newDate ? `/green-lots/on-hand?as_of=${newDate}` : "/green-lots/on-hand";
      const { data } = await api.get(url);
      setOnHand(data);
    } finally { setAsOfBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/green-lots", {
        ...form,
        weight_kg: parseFloat(form.weight_kg) || 0,
        unit_cost_per_kg: parseFloat(form.unit_cost_per_kg) || 0,
      });
      setOpen(false);
      setForm({ ...form, lot_ref: "", weight_kg: "", unit_cost_per_kg: "", notes: "" });
      await load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this FIFO lot?")) return;
    await api.delete(`/green-lots/${id}`);
    await load();
  };

  const removeAdj = async (id) => {
    if (!window.confirm("Delete this adjustment?")) return;
    await api.delete(`/inventory-adjustments/${id}`);
    await load();
  };

  const totalOnHand = onHand.reduce((s, x) => s + x.on_hand_kg, 0);
  const totalValue = onHand.reduce((s, x) => s + (x.value_zar || 0), 0);
  const originName = (id) => origins.find((o) => o.id === id)?.name || "—";

  const onUploadFifo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm(
      `Re-load green inventory from "${file.name}"?\n\n` +
      "This will REPLACE all existing FIFO lots and rebuild opening-balance " +
      "adjustments. Existing manual purchases not in the sheet will be lost."
    )) {
      e.target.value = "";
      return;
    }
    setImporting(true);
    setImportResult(null);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/inventory/import-fifo-xlsx", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      await load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Import failed");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  return (
    <div data-testid="inventory-page">
      <PageHeader
        eyebrow="Operations"
        title="Green Bean Inventory"
        description="FIFO purchase lots, manual adjustments and on-hand by origin. Click any origin to open its full transaction ledger."
        actions={(
          <div className="flex items-center gap-2">
            {isOwner && (
              <>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx"
                  className="hidden"
                  data-testid="inventory-fifo-file"
                  onChange={onUploadFifo}
                />
                <button
                  data-testid="inventory-reimport-fifo"
                  onClick={() => fileRef.current?.click()}
                  disabled={importing}
                  className="inline-flex items-center gap-2 px-3 py-2 border border-border rounded text-sm font-semibold hover:bg-secondary disabled:opacity-50"
                  title="Replace all FIFO lots with the contents of a Brothers Coffee FIFO .xlsx workbook"
                >
                  <Upload className="w-4 h-4" /> {importing ? "Importing…" : "Re-import FIFO"}
                </button>
              </>
            )}
            {canWrite && (
              <button
                data-testid="inventory-add-lot"
                onClick={() => setOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)]"
              >
                <Plus className="w-4 h-4" /> Add purchase lot
              </button>
            )}
          </div>
        )}
      />

      {importResult && (
        <div className="mb-4 bg-card border border-border rounded p-4" data-testid="inventory-import-result">
          <p className="font-display font-bold mb-2">
            FIFO re-import complete · {importResult.inserted_lots} lots · {fmt(importResult.total_kg, 1)} kg · R {fmt(importResult.total_value_zar, 0)}
          </p>
          <p className="text-xs text-muted-foreground">
            Wiped {importResult.wiped_lots} previous lots · {importResult.opening_balance_adjustments} opening-balance adjustments · {importResult.skipped_rows} non-data rows skipped.
          </p>
        </div>
      )}

      {error && <p className="mb-3 text-destructive text-sm" data-testid="inventory-error">{error}</p>}

      <div className="bg-card border border-border rounded p-3 mb-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="inventory-as-of">
        <div className="flex items-center gap-2">
          <p className="label-tiny">As at end of</p>
          <input
            type="date"
            value={asOf}
            onChange={(e) => reloadAsOf(e.target.value)}
            disabled={asOfBusy}
            data-testid="inventory-as-of-input"
            className="px-2 py-1.5 bg-white border border-border rounded text-sm focus:outline-none focus:border-primary"
          />
          {asOf && (
            <button onClick={() => reloadAsOf("")} data-testid="inventory-as-of-clear"
              className="text-xs px-2 py-1 rounded hover:bg-secondary text-muted-foreground">
              Clear
            </button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {asOf
            ? `Showing inventory state at close of ${new Date(asOf).toLocaleDateString("en-ZA")} (purchases & consumption after this date are ignored).`
            : "Showing live inventory. Pick a past date to value inventory at a point in time — useful for month-end reconciliation."}
          {asOfBusy && " · Recomputing…"}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-testid="inventory-totals">
        <Stat label={asOf ? `On-hand at ${new Date(asOf).toLocaleDateString("en-ZA")}` : "Total on-hand"}
          value={`${fmt(totalOnHand, 1)} kg`} />
        <Stat label={asOf ? `Inventory value at ${new Date(asOf).toLocaleDateString("en-ZA")} (FIFO)` : "Inventory value (FIFO)"}
          value={`R ${fmt(totalValue, 0)}`} />
      </div>

      <div className="flex gap-2 mb-4">
        <TabBtn active={tab === "on-hand"} onClick={() => setTab("on-hand")} testid="tab-on-hand">On-hand by origin</TabBtn>
        <TabBtn active={tab === "lots"} onClick={() => setTab("lots")} testid="tab-lots">FIFO lots</TabBtn>
        <TabBtn active={tab === "adjustments"} onClick={() => setTab("adjustments")} testid="tab-adjustments">Adjustments</TabBtn>
      </div>

      {tab === "lots" && (
        <p className="text-xs text-muted-foreground mb-3 bg-secondary/40 border border-border rounded p-3" data-testid="fifo-help">
          <strong>FIFO lots</strong> = your green-coffee purchase batches. Each lot has a weight and unit cost. When you roast, the oldest lot's stock is consumed first (First-In-First-Out), giving you accurate cost-per-kg over time and a true inventory value.
        </p>
      )}

      {tab === "on-hand" && (
        <div className="bg-card border border-border rounded overflow-x-auto">
          <table className="w-full text-sm" data-testid="on-hand-table">
            <thead>
              <tr className="bg-secondary">
                <th className="label-tiny px-4 py-3 text-left">Origin</th>
                <th className="label-tiny px-4 py-3 text-left">Country</th>
                <th className="label-tiny px-4 py-3 text-right">Purchased</th>
                <th className="label-tiny px-4 py-3 text-right">Adjusted</th>
                <th className="label-tiny px-4 py-3 text-right">Consumed</th>
                <th className="label-tiny px-4 py-3 text-right">On-hand (kg)</th>
                <th className="label-tiny px-4 py-3 text-right">Cost / kg</th>
                <th className="label-tiny px-4 py-3 text-right">Value (R)</th>
                <th className="w-16"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>}
              {!loading && onHand.map((x) => (
                <tr key={x.origin_id} className="border-t border-border hover:bg-secondary/40 cursor-pointer"
                    onClick={() => setOpenOriginId(x.origin_id)} data-testid={`onhand-row-${x.origin_id}`}>
                  <td className="px-4 py-2.5 font-semibold">{x.origin_name}</td>
                  <td className="px-4 py-2.5">{x.country}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{fmt(x.purchased_kg, 1)}</td>
                  <td className={`px-4 py-2.5 text-right tabular-nums ${x.adjusted_kg !== 0 ? "text-[hsl(31,65%,53%)]" : ""}`}>
                    {x.adjusted_kg > 0 ? "+" : ""}{fmt(x.adjusted_kg, 1)}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{fmt(x.consumed_kg, 1)}</td>
                  <td className={`px-4 py-2.5 text-right tabular-nums font-semibold ${x.on_hand_kg < 0 ? "text-destructive" : ""}`}>
                    {fmt(x.on_hand_kg, 1)}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-xs text-muted-foreground">
                    {x.unit_cost_per_kg > 0 ? `R ${fmt(x.unit_cost_per_kg)}` : "—"}
                  </td>
                  <td className={`px-4 py-2.5 text-right tabular-nums font-semibold ${x.value_zar > 0 ? "text-primary" : "text-muted-foreground"}`}>
                    R {fmt(x.value_zar, 0)}
                  </td>
                  <td className="px-2 py-2 text-right">
                    <span className="inline-flex items-center text-xs text-muted-foreground gap-1">
                      <FileText className="w-3.5 h-3.5" /> Ledger
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "lots" && (
        <div className="bg-card border border-border rounded overflow-x-auto">
          <table className="w-full text-sm" data-testid="lots-table">
            <thead>
              <tr className="bg-secondary">
                <th className="label-tiny px-4 py-3 text-left">Date</th>
                <th className="label-tiny px-4 py-3 text-left">Origin</th>
                <th className="label-tiny px-4 py-3 text-left">Lot ref</th>
                <th className="label-tiny px-4 py-3 text-right">Weight (kg)</th>
                <th className="label-tiny px-4 py-3 text-right">Cost / kg</th>
                <th className="label-tiny px-4 py-3 text-right">Total</th>
                {canWrite && <th className="w-12"></th>}
              </tr>
            </thead>
            <tbody>
              {!loading && lots.length === 0 && (
                <tr><td colSpan={canWrite ? 7 : 6} className="px-4 py-12 text-center text-muted-foreground">No FIFO lots yet.</td></tr>
              )}
              {lots.map((l) => (
                <tr key={l.id} className="border-t border-border hover:bg-secondary/40">
                  <td className="px-4 py-2.5 font-mono text-xs">{l.purchase_date}</td>
                  <td className="px-4 py-2.5">{originName(l.origin_id)}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{l.lot_ref || "—"}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{fmt(l.weight_kg, 1)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">R {fmt(l.unit_cost_per_kg)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">R {fmt(l.weight_kg * l.unit_cost_per_kg)}</td>
                  {canWrite && (
                    <td className="px-2 py-2 text-right">
                      <button onClick={() => remove(l.id)} className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "adjustments" && (
        <div className="bg-card border border-border rounded overflow-x-auto">
          <table className="w-full text-sm" data-testid="adjustments-table">
            <thead>
              <tr className="bg-secondary">
                <th className="label-tiny px-4 py-3 text-left">Date</th>
                <th className="label-tiny px-4 py-3 text-left">Origin</th>
                <th className="label-tiny px-4 py-3 text-left">Reason</th>
                <th className="label-tiny px-4 py-3 text-left">Notes</th>
                <th className="label-tiny px-4 py-3 text-right">Δ kg</th>
                <th className="label-tiny px-4 py-3 text-left">By</th>
                {canWrite && <th className="w-12"></th>}
              </tr>
            </thead>
            <tbody>
              {!loading && adjustments.length === 0 && (
                <tr><td colSpan={canWrite ? 7 : 6} className="px-4 py-12 text-center text-muted-foreground">
                  No adjustments yet. Open an origin's ledger to add one.
                </td></tr>
              )}
              {adjustments.map((a) => (
                <tr key={a.id} className="border-t border-border hover:bg-secondary/40">
                  <td className="px-4 py-2.5 font-mono text-xs">{a.adjustment_date}</td>
                  <td className="px-4 py-2.5">{originName(a.origin_id)}</td>
                  <td className="px-4 py-2.5">{a.reason}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{a.notes || "—"}</td>
                  <td className={`px-4 py-2.5 text-right tabular-nums font-semibold ${a.delta_kg >= 0 ? "text-[hsl(132,18%,36%)]" : "text-destructive"}`}>
                    {a.delta_kg > 0 ? "+" : ""}{fmt(a.delta_kg, 2)}
                  </td>
                  <td className="px-4 py-2.5 text-xs">{a.created_by || "—"}</td>
                  {canWrite && (
                    <td className="px-2 py-2 text-right">
                      <button onClick={() => removeAdj(a.id)} className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4" data-testid="lot-modal">
          <div className="bg-card border border-border rounded shadow-xl max-w-lg w-full">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h3 className="font-display text-lg font-bold">New green coffee purchase lot</h3>
              <button onClick={() => setOpen(false)}><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-4">
              <Field label="Origin *">
                <select required value={form.origin_id} onChange={(e) => setForm({ ...form, origin_id: e.target.value })} data-testid="lot-origin"
                  className="w-full px-3 py-2 bg-white border border-border rounded text-sm focus:outline-none focus:border-primary">
                  <option value="">Select origin</option>
                  {origins.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </Field>
              <Field label="Supplier">
                <select value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-border rounded text-sm focus:outline-none focus:border-primary">
                  <option value="">—</option>
                  {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Purchase date *">
                  <input required type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} data-testid="lot-date"
                    className="w-full px-3 py-2 bg-white border border-border rounded text-sm focus:outline-none focus:border-primary" />
                </Field>
                <Field label="Lot reference">
                  <input value={form.lot_ref} onChange={(e) => setForm({ ...form, lot_ref: e.target.value })}
                    className="w-full px-3 py-2 bg-white border border-border rounded text-sm focus:outline-none focus:border-primary" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Weight (kg) *">
                  <input required type="number" step="any" value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} data-testid="lot-weight"
                    className="w-full px-3 py-2 bg-white border border-border rounded text-sm focus:outline-none focus:border-primary" />
                </Field>
                <Field label="Cost / kg (R) *">
                  <input required type="number" step="any" value={form.unit_cost_per_kg} onChange={(e) => setForm({ ...form, unit_cost_per_kg: e.target.value })} data-testid="lot-cost"
                    className="w-full px-3 py-2 bg-white border border-border rounded text-sm focus:outline-none focus:border-primary" />
                </Field>
              </div>
              {error && <p className="text-destructive text-sm">{error}</p>}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border border-border rounded text-sm hover:bg-secondary">Cancel</button>
                <button type="submit" data-testid="lot-submit" className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)]">Save lot</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {openOriginId && (
        <OriginTransactionsModal
          originId={openOriginId}
          canWrite={canWrite}
          onClose={() => setOpenOriginId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-card border border-border rounded p-4">
      <p className="label-tiny mb-1">{label}</p>
      <p className="font-display text-2xl font-bold tabular-nums">{value}</p>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-2 text-sm font-semibold rounded border ${active ? "bg-primary text-primary-foreground border-primary" : "border-border bg-card hover:bg-secondary"}`}>
      {children}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label-tiny mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

